import { 
  Download, 
  Image as ImageIcon, 
  FileText, 
  File as FileIcon,
  FileSpreadsheet,
  Presentation
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import chatFileUploadService from '@/services/chatFileUploadService'
import { useTranslation } from 'react-i18next'

interface ChatFileMessageProps {
  message: {
    id: string
    content?: string
    fileName: string
    filePath: string
    fileSize: number
    mimeType: string
    messageType: 'file' | 'image'
    senderId: string
    createdAt: string
    isRead?: boolean
    temporary?: boolean
    error?: boolean
  }
  isCurrentUser: boolean
  className?: string
}

export default function ChatFileMessage({ 
  message, 
  isCurrentUser, 
  className 
}: ChatFileMessageProps) {
  const { t } = useTranslation('common')
  
  // Get appropriate icon for file type
  const getFileIcon = () => {
    const iconClass = "h-5 w-5"
    
    switch (chatFileUploadService.getFileIcon(message.mimeType)) {
      case 'image':
        return <ImageIcon className={iconClass} />
      case 'file-text':
        return <FileText className={iconClass} />
      case 'file-spreadsheet':
        return <FileSpreadsheet className={iconClass} />
      case 'presentation':
        return <Presentation className={iconClass} />
      default:
        return <FileIcon className={iconClass} />
    }
  }

  // Handle file download
  const handleDownload = () => {
    // Create a temporary link to download the file
    const link = document.createElement('a')
    link.href = message.filePath
    link.download = message.fileName
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Render image preview for image files
  const renderImagePreview = () => {
    if (message.messageType !== 'image') return null
    
    // Don't show image preview if message is temporary (still uploading)
    if (message.temporary) return null

    return (
      <div className="mt-3 relative group">
        <img
          src={message.filePath}
          alt={message.fileName}
          className="w-full max-w-sm rounded-lg shadow-sm cursor-pointer hover:shadow-md transition-all duration-200"
          onClick={handleDownload}
          onError={(e) => {
            // If image fails to load, show file icon instead
            const target = e.target as HTMLImageElement
            target.style.display = 'none'
            const parent = target.parentElement
            if (parent) {
              parent.innerHTML = `
                <div class="flex items-center justify-center h-32 w-full bg-gray-100 rounded-lg">
                  <svg class="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                </div>
              `
            }
          }}
        />
        
        {/* Download overlay on hover */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
            <Button
                variant="secondary"
                size="sm"
                className="bg-white/90 hover:bg-white text-gray-800 shadow-lg"
                onClick={handleDownload}
            >
                <Download className="h-4 w-4 mr-2" />
                {t('chat.fileMessage.download')}
            </Button>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'max-w-[85%] rounded-2xl overflow-hidden',
        isCurrentUser
          ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white ml-auto shadow-lg'
          : 'bg-white shadow-md border border-gray-100',
        className
      )}
    >
      {/* Image preview first for better visual hierarchy */}
      {renderImagePreview()}

      {/* File info */}
      <div className="p-4">
        <div className="flex items-center space-x-3">
          <div className={cn(
            'flex-shrink-0 p-3 rounded-xl',
            isCurrentUser 
              ? 'bg-white/20 backdrop-blur-sm' 
              : 'bg-blue-50'
          )}>
            <div className={cn(
              'text-lg',
              isCurrentUser ? 'text-white' : 'text-blue-600'
            )}>
              {getFileIcon()}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p className={cn(
                  'text-sm font-semibold truncate',
                  isCurrentUser ? 'text-white' : 'text-gray-900'
                )}>
                  {message.fileName}
                </p>
                
                <p className={cn(
                  'text-xs mt-1 font-medium',
                  isCurrentUser ? 'text-white/80' : 'text-gray-500'
                )}>
                  {chatFileUploadService.formatFileSize(message.fileSize)}
                </p>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-8 w-8 flex-shrink-0 ml-3 rounded-full',
                  isCurrentUser 
                    ? 'text-white hover:bg-white/20 backdrop-blur-sm' 
                    : 'text-gray-600 hover:bg-gray-100'
                )}
                onClick={handleDownload}
                title={t('chat.fileMessage.downloadFile')}
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Optional text message */}
        {message.content && (
          <div className="mt-3 pt-3 border-t border-white/20">
            <p className="text-sm leading-relaxed">{message.content}</p>
          </div>
        )}

        {/* Message metadata */}
        <div className={cn(
          'text-xs mt-3 flex justify-between items-center',
          isCurrentUser ? 'text-white/70' : 'text-gray-400'
        )}>
          <span className="font-medium">
            {format(new Date(message.createdAt), 'HH:mm')}
          </span>
          
          <div className="flex items-center space-x-2">
            {message.temporary && (
              <span className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-current rounded-full animate-pulse"></div>
                <span>{t('chat.fileMessage.sending')}</span>
              </span>
            )}
            {message.error && (
              <span className="text-red-400 font-medium">{t('chat.fileMessage.failed')}</span>
            )}
            {!message.temporary && !message.error && isCurrentUser && message.isRead && (
                <span className="text-white/60" style={{ letterSpacing: '1px' }}>✓</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}